import React, { useEffect, useState } from 'react';

import Close from '@mui/icons-material/Close';
import DeleteRounded from '@mui/icons-material/DeleteRounded';
import {
  Alert,
  Grid, IconButton, Stack, Typography,
} from '@mui/material';

import AdditionModal from '../../../components/AdditionModal';
import Button from '../../../components/Button';
import CommitForm from '../../../components/CommitForm';
import ContentCard from '../../../components/ContentCard';
import Modal from '../../../components/Modal';
import PermissionsList from '../../../components/PermissionsList';
import {
  useAddPermissionToLessonTemplateMutation,
  useDeleteLessonTemplateMutation,
  useGetLessonTemplateQuery,
  useRemovePermissionFromLessonTemplateMutation,
  useUpdateLessonTemplateMutation,
} from '../../../services/lessons';
import { useGetPermissionsQuery } from '../../../services/permissions';
import { Permission } from '../../../services/permissions/types';
import useError from '../../../utils/react/hooks/useError';
import useSuccess from '../../../utils/react/hooks/useSuccess';

interface LessonDetailsProps {
  course?: string,
  lesson?: string,
  onClose: () => void,
}

const LessonDetails = ({ course, lesson, onClose }: LessonDetailsProps) => {
  const [addPermissionModalOpen, setAddPermissionModalOpen] = useState(false);
  const [addPermissionModalSearchStr, setAddPermissionModalSearchStr] = useState('');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const [detailedLessonPermissions, setDetailedLessonPermissions] = useState<Permission[]>([]);
  const [targetPermissionId, setTargetPermissionId] = useState<string | undefined>(undefined);

  const { data: lessonData } = useGetLessonTemplateQuery({
    course: course || '',
    lessonId: lesson || '',
  }, { skip: !course || !lesson });
  const [updateLessonTemplate, {
    isLoading: isUpdateLessonTemplateLoading,
  }] = useUpdateLessonTemplateMutation();
  const [addPermissionToLesson, {
    isSuccess: isAddPermissionToLessonTemplateSuccess,
    isLoading: isAddPermissionToLessonTemplateLoading,
    isError: isAddPermissionToLessonTemplateError,
    error: addPermissionToLessonTemplateError,
    reset: resetAddPermissionToLessonTemplate,
  }] = useAddPermissionToLessonTemplateMutation();
  const [removePermissionFromLesson, {
    isSuccess: isRemovePermissionFromLessonTemplateSuccess,
    isLoading: isRemovePermissionFromLessonTemplateLoading,
    isError: isRemovePermissionFromLessonTemplateError,
    error: removePermissionFromLessonTemplateError,
  }] = useRemovePermissionFromLessonTemplateMutation();
  const [deleteLessonTemplate, {
    isLoading: isDeleteLessonTemplateLoading,
    isSuccess: isDeleteLessonTemplateSuccess,
    isError: isDeleteLessonTemplateError,
    error: deleteLessonTemplateError,
  }] = useDeleteLessonTemplateMutation();
  const { data: permissionsList } = useGetPermissionsQuery({});

  useEffect(() => {
    if (lessonData?.data.permissions && permissionsList) {
      const detailedPermissions = lessonData?.data.permissions.map((p) =>
        permissionsList.data.find((x) => x.value === p),
      ).filter((x) => x !== undefined) as Permission[];

      setDetailedLessonPermissions(detailedPermissions);
    }
  }, [lessonData?.data.permissions, permissionsList]);

  useEffect(() => {
    if (isDeleteLessonTemplateSuccess) {
      onClose();
      setDeleteModalOpen(false);
    }
  }, [isDeleteLessonTemplateSuccess]);

  useEffect(() => {
    if (isDeleteLessonTemplateError) {
      setDeleteModalOpen(false);
    }
  }, [isDeleteLessonTemplateError]);

  useError(isAddPermissionToLessonTemplateError, addPermissionToLessonTemplateError);
  useError(isRemovePermissionFromLessonTemplateError, removePermissionFromLessonTemplateError);
  useError(isDeleteLessonTemplateError, deleteLessonTemplateError);
  useSuccess(isAddPermissionToLessonTemplateSuccess, 'Permission added');
  useSuccess(isRemovePermissionFromLessonTemplateSuccess, 'Permission removed');
  useSuccess(isDeleteLessonTemplateSuccess, 'Lesson template deleted');

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
        >
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
          <Typography variant="h6">
            Lesson Details
          </Typography>
        </Stack>
      </Grid>
      <Grid item xs={12}>
        <ContentCard title="Lesson Details">
          <CommitForm
            data={lessonData?.data}
            metadata={lessonData?.metadata}
            fields={{
              course: {
                xs: 12,
              },
              lessonType: {
                xs: 12,
              },
              lessonGroup: {
                xs: 12,
              },
              label: {
                xs: 12,
              },
              isTimesheeted: {
                xs: 12,
              },
            }}
            onSubmit={(values) => {
              updateLessonTemplate({
                course: course || '',
                lessonId: lesson || '',
                newAttributes: values,
              });
            }}
            isSubmitLoading={isUpdateLessonTemplateLoading}
            autoSubmit
          />
        </ContentCard>
      </Grid>
      <Grid item xs={12}>
        <ContentCard title="Permissions" variant="outlined">
          <Stack direction="column" spacing={2}>
            <Alert severity="warning">
              Updating permissions tied to a lesson affects future instances of the lesson
              only. <b>Past lessons are NOT affected.</b>
            </Alert>
            <PermissionsList
              data={detailedLessonPermissions}
              columns={[{
                field: 'label',
                headerName: 'Label',
                width: 400,
              },
              {
                field: 'control',
                headerName: 'Control',
                width: 100,
                renderCell: ({ row }: any) => (
                  <Button
                    size="small"
                    color="error"
                    variant="text"
                    allCaps
                    disabled={isRemovePermissionFromLessonTemplateLoading}
                    loading={isRemovePermissionFromLessonTemplateLoading
                                  && targetPermissionId === row.id}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setTargetPermissionId(row.id);
                      removePermissionFromLesson({
                        course: course || '',
                        lessonId: lesson || '',
                        permissionId: row.id || '',
                      });
                    }}
                  >
                    Unshare
                  </Button>
                ),
              }]}
              addButtonProps={{
                disabled: isAddPermissionToLessonTemplateLoading,
              }}
              onAddClick={() => setAddPermissionModalOpen(true)}
              getRowHref={(row: Permission) => `/admin/permission/${row.id}`}
            />
          </Stack>
        </ContentCard>
      </Grid>
      <Grid item xs={12}>
        <ContentCard title="Actions" variant="outlined">
          <Button
            color="error"
            onClick={() => setDeleteModalOpen(true)}
            startIcon={(<DeleteRounded />)}
          >
            Delete
          </Button>
        </ContentCard>
      </Grid>
      <Modal
        open={deleteModalOpen}
        handleClose={() => setDeleteModalOpen(false)}
        title="Are you sure?"
        description="This will permamently delete this lesson template."
        buttons={[
          {
            label: 'Delete',
            onClick: () => deleteLessonTemplate({
              course: course || '',
              lessonId: lesson || '',
            }),
            color: 'error',
            loading: isDeleteLessonTemplateLoading,
          },
        ]}
      />
      <AdditionModal
        title="Add Permission"
        searchPlaceholder="Permission"
        open={addPermissionModalOpen}
        handleClose={() => {
          setAddPermissionModalOpen(false);
        }}
        onAdd={
          (x: Permission) => {
            addPermissionToLesson({
              course: course || '',
              lessonId: lesson || '',
              permissionId: x.value || '',
            });
          }
        }
        isLoading={isAddPermissionToLessonTemplateLoading}
        isSuccess={isAddPermissionToLessonTemplateSuccess}
        isError={isAddPermissionToLessonTemplateError}
        error={(addPermissionToLessonTemplateError as any)?.data?.message}
        closeNotifs={resetAddPermissionToLessonTemplate}
        searchFieldProps={{
          options: (permissionsList?.data || []).filter((p) => {
            const isExistingPermission = lessonData?.data.permissions.some(
              (x) => x === p.value,
            );
            return !isExistingPermission;
          }).filter(
            (p) => p.label.toLowerCase().includes(addPermissionModalSearchStr.toLowerCase()),
          ),
          getOptionLabel: (option: Permission) => option.label,
          getOptionKey: (option: Permission) => option.value,
          onSearch: (s) => setAddPermissionModalSearchStr(s),
        }}
      />
    </Grid>
  );
};

export default LessonDetails;
